'use client';

import { cn } from '@ahm/common-helpers';
import { motion } from 'framer-motion';
import { useRouter, usePathname } from 'next/navigation';
import { useCallback, useTransition } from 'react';
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { trackEvent } from '@/lib/event-tracking';
import { useAuth } from '@/providers/auth-provider';

interface TabSwitchProps {
  className?: string;
}

export default function TabSwitch({ className }: TabSwitchProps) {
  const pathname = usePathname();
  const router = useRouter();
  const routes = useCityRoutes();
  const { user } = useAuth();
  const [isPending, startTransition] = useTransition();

  const isSoloMealMode = pathname.includes('/solomeal');

  const handleNavigate = useCallback(
    (tabType: 'store' | 'solomeal', href: string) => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      trackEvent('click_button', {
        action_time: Math.floor(Date.now() / 1000),
        button_name: tabType === 'store' ? 'store_tab' : 'solomeal_tab',
        user_id: user?.id ?? '',
      });

      // Use startTransition for smoother navigation with loading state
      startTransition(() => {
        router.push(href);
      });
    },
    [router, user?.id]
  );

  // Prefetch on hover for instant navigation
  const handlePrefetch = useCallback(
    (href: string) => {
      router.prefetch(href);
    },
    [router]
  );

  return (
    <div
      className={cn(
        'relative flex items-center bg-neutral-10 rounded-2xl p-2 shadow-sm overflow-hidden',
        { 'opacity-70 pointer-events-none': isPending },
        className
      )}
    >
      {/* Hiệu ứng nền trượt */}
      <motion.div
        layout
        layoutId="activeTab"
        className="absolute inset-y-2 w-[calc(50%-0.5rem)] bg-white rounded-xl shadow-sm"
        style={{
          left: isSoloMealMode ? '8px' : '50%',
        }}
        transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30,
        }}
      />

      {/* Tab SoloMeal */}
      <button
        onClick={() => handleNavigate('solomeal', routes.solomeal)}
        onMouseEnter={() => handlePrefetch(routes.solomeal)}
        onTouchStart={() => handlePrefetch(routes.solomeal)}
        disabled={isPending || isSoloMealMode}
        className={cn(
          'relative z-10 flex-1 flex justify-center items-center px-4 py-auto rounded-xl transition-all duration-200',
          isSoloMealMode ? 'text-primary-50' : 'text-neutral-60',
          'disabled:cursor-not-allowed'
        )}
      >
        <img
          className="h-5 w-auto mb-1"
          src={
            isSoloMealMode
              ? '/static/icons/solomeal-tag.svg'
              : '/static/icons/solomeal-tab-inactive.svg'
          }
          alt="SoloMeal"
        />
      </button>

      {/* Tab Store */}
      <button
        onClick={() => handleNavigate('store', routes.home)}
        onMouseEnter={() => handlePrefetch(routes.home)}
        onTouchStart={() => handlePrefetch(routes.home)}
        disabled={isPending || !isSoloMealMode}
        className={cn(
          'relative z-10 flex-1 px-4 py-1 text-base font-bold text-center rounded-xl transition-all duration-200',
          !isSoloMealMode ? 'text-primary-50' : 'text-neutral-60',
          'disabled:cursor-not-allowed'
        )}
      >
        Quán ăn
      </button>
    </div>
  );
}
